import { z } from 'zod';
import type {
  FieldErrors,
  UseFormRegister,
  UseFormSetValue,
  UseFormWatch,
  UseFormTrigger,
} from 'react-hook-form';

import { WORKING_DAY_TYPE_ID } from '@/api/domains/catalog/workingDayType';


export const STATIC_WORKING_DAY_TYPES: number[] = [
  WORKING_DAY_TYPE_ID.FIXED,
  WORKING_DAY_TYPE_ID.BY_SHIFT,
  WORKING_DAY_TYPE_ID.PARTIAL,
];

const createShiftDescriptionBaseSchema = z.object({
  workingDayTypeId: z.union([
    z.coerce.number().int().positive(),
    z.null(),
  ]),
  workingDayId: z.union([
    z.coerce.number().int().positive(),
    z.null(),
  ]),
  shiftCode: z.string()
    .min(1, 'schedule:shift.create.validation.shiftCodeRequired')
    .max(200, 'schedule:shift.create.validation.shiftCodeMaxLength'),
  shiftName: z.string()
    .min(1, 'schedule:shift.create.validation.shiftNameRequired')
    .max(500, 'schedule:shift.create.validation.shiftNameMaxLength'),
  weeksCount: z.coerce
    .number({ invalid_type_error: 'schedule:shift.create.validation.weeksCountRequired' })
    .int()
    .min(1, 'schedule:shift.create.validation.weeksCountRequired'),
  isExceptionalWorkingDay: z.boolean().optional().default(false),
  dtAuthorizationNumber: z.string().optional().default(''),
  dtAuthorizationDate: z.string().optional().default(''),
  staticStartDate: z.string().optional().default(''),
});

export const createShiftDescriptionSchema = createShiftDescriptionBaseSchema.superRefine(
  (data, ctx) => {
    if (data.workingDayTypeId === null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'schedule:shift.create.validation.workingDayTypeRequired',
        path: ['workingDayTypeId'],
      });
    }

    if (data.workingDayId === null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'schedule:shift.create.validation.workingDayRequired',
        path: ['workingDayId'],
      });
    }

    const isDistributedCyclesExceptional =
      data.workingDayTypeId === WORKING_DAY_TYPE_ID.DISTRIBUTED_CYCLES &&
      data.isExceptionalWorkingDay;
    const requiresDtAuthorization =
      data.workingDayTypeId === WORKING_DAY_TYPE_ID.EXCEPTIONAL || isDistributedCyclesExceptional;

    if (requiresDtAuthorization) {
      if (!data.dtAuthorizationNumber.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'schedule:shift.create.validation.dtAuthorizationNumberRequired',
          path: ['dtAuthorizationNumber'],
        });
      }
      if (!data.dtAuthorizationDate.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'schedule:shift.create.validation.dtAuthorizationDateRequired',
          path: ['dtAuthorizationDate'],
        });
      }
    }

    if (data.dtAuthorizationDate.trim()) {
      const parsedDate = new Date(data.dtAuthorizationDate + 'T00:00:00');
      if (!Number.isNaN(parsedDate.getTime()) && parsedDate.getTime() > Date.now()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'schedule:shift.create.validation.dtAuthorizationDateFuture',
          path: ['dtAuthorizationDate'],
        });
      }
    }

    if (
      data.workingDayTypeId !== null &&
      STATIC_WORKING_DAY_TYPES.includes(data.workingDayTypeId) &&
      !data.staticStartDate.trim()
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'schedule:shift.create.validation.staticStartDateRequired',
        path: ['staticStartDate'],
      });
    }
  },
);

export type CreateShiftDescriptionForm = z.infer<typeof createShiftDescriptionSchema>;

export interface CreateShiftDescriptionFormSubset {
  register: UseFormRegister<CreateShiftDescriptionForm>;
  setValue: UseFormSetValue<CreateShiftDescriptionForm>;
  watch: UseFormWatch<CreateShiftDescriptionForm>;
  errors: FieldErrors<CreateShiftDescriptionForm>;
  trigger: UseFormTrigger<CreateShiftDescriptionForm>;
}
